import { z } from 'zod';
import { compoundSelectionBindingSchema } from './compound-selection.contract.js';

/** Public revision token only: no task arguments or client patch. */
export const pendingTaskCorrectionPayloadSchema = z
  .object({
    type: z.literal('pending_task_correction_v1'),
    version: z.literal(1),
    mode: z.enum(['simple', 'compound']),
    timeZone: z.string().min(1).max(100).optional(),
    binding: compoundSelectionBindingSchema.omit({ sourceHash: true }),
  })
  .strict();
export type PendingTaskCorrectionPayload = z.infer<typeof pendingTaskCorrectionPayloadSchema>;
export const taskCorrectionRequestSchema = z
  .object({
    sourceMessageId: z.string().trim().min(1).max(200),
  })
  .strict();
export type TaskCorrectionRequest = z.infer<typeof taskCorrectionRequestSchema>;
export type SingleTaskCorrection =
  | {
      kind: 'change';
      field: 'title' | 'dueAt' | 'contact';
      value: string | null;
      sourceQuote: string;
    }
  | { kind: 'clarify'; fields: readonly string[] };
const allFields = ['title', 'dueAt', 'contactId'];
const prefixes = [
  ['название:', 'title'],
  ['переименуй в ', 'title'],
  ['title:', 'title'],
  ['rename to ', 'title'],
  ['нет, на ', 'dueAt'],
  ['перенеси на ', 'dueAt'],
  ['no, on ', 'dueAt'],
  ['move to ', 'dueAt'],
  ['reschedule to ', 'dueAt'],
] as const;
const contactClears = new Set([
  'без контакта',
  'убери контакт',
  'without contact',
  'remove contact',
]);
const shortDateWords = new Set([
  'сегодня',
  'завтра',
  'послезавтра',
  'понедельник',
  'вторник',
  'среда',
  'среду',
  'четверг',
  'пятница',
  'пятницу',
  'суббота',
  'субботу',
  'воскресенье',
  'today',
  'tomorrow',
  'day after tomorrow',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);
const decimalDigits = (value: string) =>
  value.length > 0 && [...value].every((char) => char >= '0' && char <= '9');
/** Lexical clock only, no relative date or free-text inference. */
export function isTaskCorrectionClock(value: string): boolean {
  const lower = value.trim().toLowerCase();
  const suffix = lower.endsWith('am') || lower.endsWith('pm');
  if (suffix) {
    const clock = lower.slice(0, -2).trim();
    const parts = clock.split(':');
    return (
      parts.length <= 2 &&
      decimalDigits(parts[0]!) &&
      Number(parts[0]) >= 1 &&
      Number(parts[0]) <= 12 &&
      (parts.length === 1 ||
        (parts[1]!.length === 2 && decimalDigits(parts[1]!) && Number(parts[1]) <= 59))
    );
  }
  const parts = lower.split(':');
  return (
    parts.length === 2 &&
    decimalDigits(parts[0]!) &&
    parts[0]!.length <= 2 &&
    Number(parts[0]) <= 23 &&
    parts[1]!.length === 2 &&
    decimalDigits(parts[1]!) &&
    Number(parts[1]) <= 59
  );
}
function shortDateCorrection(source: string): SingleTaskCorrection | null {
  const lower = source.toLowerCase();
  const prefix = ['на ', 'в ', 'to ', 'at '].find((value) => lower.startsWith(value));
  if (!prefix) return null;
  const value = source.slice(prefix.length).trim();
  return shortDateWords.has(value.toLowerCase()) || isTaskCorrectionClock(value)
    ? { kind: 'change', field: 'dueAt', value, sourceQuote: value }
    : null;
}
/** Whole-field table, no new phrase regex or model inference. */
export function singleTaskCorrection(text: string): SingleTaskCorrection | null {
  const source = text.trim();
  const lower = source.toLocaleLowerCase();
  if (contactClears.has(lower))
    return { kind: 'change', field: 'contact', value: null, sourceQuote: source };
  if (
    [...contactClears].some(
      (clear) =>
        lower.startsWith(clear) && [' ', ';', ',', '\n'].includes(lower.charAt(clear.length)),
    )
  )
    return { kind: 'clarify', fields: allFields };
  const prefix = prefixes.find(([value]) => lower.startsWith(value));
  if (!prefix)
    return lower === 'нет' || lower === 'no'
      ? { kind: 'clarify', fields: allFields }
      : shortDateCorrection(source);
  const value = source.slice(prefix[0].length).trim();
  // Two fields/actions are never silently reduced to the first one.
  const otherMarkers = [
    ';',
    '\n',
    ' название:',
    ' title:',
    ' без контакта',
    ' without contact',
    ' переименуй ',
    ' rename to ',
    ' перенеси ',
    ' move to ',
  ];
  if (
    !value ||
    source.length > 2000 ||
    otherMarkers.some((marker) => value.toLocaleLowerCase().includes(marker))
  )
    return { kind: 'clarify', fields: allFields };
  return { kind: 'change', field: prefix[1], value, sourceQuote: value };
}
export function taskCorrectionForMessage(
  messages: readonly { id: string; role: string; structuredPayload?: unknown }[],
  text: string,
): TaskCorrectionRequest | undefined {
  if (!singleTaskCorrection(text)) return undefined;
  const last = [...messages]
    .reverse()
    .find((row) => row.role === 'assistant' || row.role === 'user');
  return last?.role === 'assistant' &&
    pendingTaskCorrectionPayloadSchema.safeParse(last.structuredPayload).success
    ? { sourceMessageId: last.id }
    : undefined;
}
